<template>
    <Dialog ref="dialog" size="xl">
        <template v-slot:title>Převzetí hodnot z podkladů</template>
        <template v-slot:body>
            <template v-if="saved !== null">
                <div class="alert alert-success d-flex align-items-center">
                    <span class="fas fa-check-circle me-2"></span>
                    Uloženo {{ saved.count }} {{ plural(saved.count, 'hodnota', 'hodnoty', 'hodnot') }}, převzatá pole ověřte ve formuláři.
                </div>
                <h6 class="text-muted text-uppercase small fw-semibold mb-2">Zbývá vyplnit</h6>
                <div class="list-group">
                    <div v-for="group in saved.missing" :key="group.path" class="list-group-item d-flex gap-3 align-items-center">
                        <div class="flex-grow-1 min-width-0">
                            <div class="fw-semibold">{{ group.label }}</div>
                            <div class="small text-muted">{{ group.names.map(parameterName).join(', ') }}</div>
                        </div>
                        <button type="button" class="btn btn-sm btn-outline-primary flex-shrink-0" @click="showSegment(group.path)">
                            Přejít
                        </button>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="d-flex align-items-start justify-content-between gap-3 mb-2">
                    <div class="small text-muted">
                        Zaškrtněte, co chcete uložit do formuláře. Co nezaškrtnete, zůstane tu na později.
                    </div>
                    <select v-if="sourceOptions.length > 1" class="form-select form-select-sm w-auto" v-model="sourceId">
                        <option :value="null">Všechny podklady</option>
                        <option v-for="source in sourceOptions" :key="source.id" :value="source.id">{{ source.name }}</option>
                    </select>
                </div>

                <div v-if="errorMessage !== null" class="alert alert-danger">{{ errorMessage }}</div>

                <div v-if="found.rows.length === 0" class="text-muted py-3">
                    V podkladech není nic, co by se lišilo od formuláře.
                </div>

                <template v-for="group in groups" :key="group.label">
                    <h6 class="text-muted text-uppercase small fw-semibold mt-3 mb-2">{{ group.label }}</h6>
                    <div class="list-group">
                        <div v-for="row in group.rows" :key="row.name" class="list-group-item d-flex gap-3 align-items-start">
                            <div class="found-values-label flex-shrink-0">
                                <div class="fw-semibold">{{ parameterName(row.name) }}</div>
                                <div class="small text-muted">
                                    teď: {{ row.current === null || row.current === undefined ? 'nevyplněno' : formatValue(row.name, row.current) }}
                                </div>
                            </div>

                            <div class="flex-grow-1 min-width-0">
                                <template v-if="row.kind === 'multi'">
                                    <div v-for="(item, index) in row.items" :key="index" class="form-check">
                                        <input class="form-check-input" type="checkbox" :id="inputId(row, index)"
                                               :checked="isItemSelected(row, item.item)"
                                               @change="toggleItem(row, item.item)">
                                        <label class="form-check-label" :for="inputId(row, index)">
                                            + {{ formatItem(row.name, item.item) }}
                                        </label>
                                        <FoundValueOrigins :origins="item.origins" :source-names="sourceNames"/>
                                    </div>
                                </template>

                                <template v-else-if="row.kind === 'parts'">
                                    <div v-for="part in row.parts" :key="part.key" class="border rounded px-3 py-2 mb-2">
                                        <div class="d-flex align-items-center gap-2 flex-wrap">
                                            <button type="button" class="btn btn-link p-0 text-body text-decoration-none"
                                                    :aria-expanded="isPartExpanded(part.key)"
                                                    @click="togglePartExpanded(part.key)">
                                                <span class="fas fa-fw me-2" :class="isPartExpanded(part.key) ? 'fa-chevron-down' : 'fa-chevron-right'"></span>{{ part.name }}
                                            </button>
                                            <span class="small text-muted">
                                                {{ part.rooms.length }} {{ plural(part.rooms.length, 'místnost', 'místnosti', 'místností') }},
                                                {{ formatNumber(part.area) }} m²
                                            </span>
                                            <span v-if="statedAreaCheck(part) !== null" class="small"
                                                  :class="statedAreaCheck(part)!.matches ? 'text-success' : 'text-warning-emphasis'">
                                                <span class="fas me-2"
                                                      :class="statedAreaCheck(part)!.matches ? 'fa-check' : 'fa-exclamation-triangle'"></span>{{
                                                    statedAreaCheck(part)!.matches
                                                        ? 'souhlasí s výkresem'
                                                        : `na výkrese ${formatNumber(part.statedArea!)} m², rozdíl ${formatSignedNumber(statedAreaCheck(part)!.difference)} m²`
                                                }}
                                            </span>
                                            <select class="form-select form-select-sm w-auto ms-auto" aria-label="Kam část patří"
                                                    :value="partChoice(row, part).target"
                                                    @change="setPartTarget(row, part, $event)">
                                                <option value="new">Nová část</option>
                                                <template v-for="(name, index) in currentPartNames(row)" :key="index">
                                                    <option :value="'add:' + index">Přidat do „{{ name }}“</option>
                                                    <option :value="'replace:' + index">Nahradit „{{ name }}“</option>
                                                </template>
                                                <option value="skip">Nepřebírat</option>
                                            </select>
                                        </div>
                                        <input v-if="partChoice(row, part).target === 'new'" type="text"
                                               class="form-control form-control-sm mt-2" aria-label="Název nové části"
                                               :value="partChoice(row, part).name"
                                               @input="setPartName(row, part, $event)">
                                        <FoundValueOrigins :origins="part.origins" :source-names="sourceNames"/>
                                        <div v-if="part.inForm" class="small text-muted">Tato část už ve formuláři je.</div>
                                        <div v-if="isPartExpanded(part.key)" class="mt-2">
                                            <div v-for="(found, index) in part.rooms" :key="index" class="form-check">
                                                <input class="form-check-input" type="checkbox" :id="roomInputId(part.key, index)"
                                                       :checked="partChoice(row, part).rooms[index]"
                                                       :disabled="partChoice(row, part).target === 'skip'"
                                                       @change="togglePartRoom(row, part, index)">
                                                <label class="form-check-label" :for="roomInputId(part.key, index)">
                                                    <template v-if="found.label !== null">{{ found.label }}, </template>{{ roomLabel(found.room.type) }},
                                                    {{ formatNumber(found.area) }} m²<span
                                                        v-if="found.duplicate !== null" class="small text-warning-emphasis"
                                                    >, {{ found.duplicate === 'form' ? 'pravděpodobně už ve formuláři' : 'pravděpodobně stejná jako v jiném podkladu' }}</span>
                                                </label>
                                            </div>
                                        </div>
                                        <div v-else-if="duplicateCount(part) > 0" class="small text-warning-emphasis">
                                            Pravděpodobně zdvojené místnosti ({{ duplicateCount(part) }}) nejsou vybrané.
                                        </div>
                                    </div>
                                    <div class="small">
                                        <template v-if="composedParts(row) !== null">
                                            Výsledek ve formuláři: <strong>{{ composedSummary(row) }}</strong>
                                        </template>
                                        <span v-else class="text-muted">Není vybraná žádná část.</span>
                                        <div class="text-muted">
                                            Nové části stejného názvu se spojí.<template v-if="hasSumOnly(row)"> Plocha zadaná
                                            součtem se nahradí.</template>
                                        </div>
                                    </div>
                                </template>

                                <template v-else>
                                    <div v-for="(option, index) in row.options" :key="index" class="form-check">
                                        <input class="form-check-input" :type="row.conflict ? 'radio' : 'checkbox'"
                                               :name="'found-value-' + row.name"
                                               :id="inputId(row, index)"
                                               :disabled="!option.valid"
                                               :checked="isOptionSelected(row, option.value)"
                                               @change="toggleOption(row, option.value)">
                                        <label class="form-check-label" :class="{'text-muted': !option.valid}" :for="inputId(row, index)">
                                            {{ formatValue(row.name, option.value) }}<span v-if="optionNote(row, option) !== null"
                                                class="small" :class="option.valid && !option.equalsCurrent ? 'text-warning-emphasis' : 'text-muted'"
                                            >, {{ optionNote(row, option) }}</span>
                                        </label>
                                        <FoundValueOrigins :origins="option.origins" :source-names="sourceNames"/>
                                    </div>
                                    <div v-if="row.conflict" class="small text-muted mt-1">
                                        Podklady se liší, vyberte správnou hodnotu.
                                        <button v-if="isOptionSelected(row, undefined) === false" type="button"
                                                class="btn btn-link btn-sm p-0 align-baseline" @click="clearSelection(row)">
                                            Nepřebírat
                                        </button>
                                    </div>
                                    <div v-if="edits[row.name] !== undefined" class="mt-2">
                                        <div class="small text-muted mb-1">Uloží se upravená hodnota:</div>
                                        <component :is="resolveControl(definitionOf(row.name).type)" v-bind="definitionOf(row.name)"
                                                   :parameter-id="row.name"
                                                   :value="edits[row.name].initial"
                                                   :label="parameterName(row.name)"
                                                   :validate="!edits[row.name].valid"
                                                   @input="editInput(row, $event)"
                                                   @validated="editValidated(row, $event)"/>
                                        <button type="button" class="btn btn-link btn-sm p-0 mt-1" @click="cancelEdit(row)">
                                            Zrušit úpravu
                                        </button>
                                    </div>
                                    <button v-else-if="row.options.some(option => option.valid)" type="button"
                                            class="btn btn-link btn-sm p-0 mt-1" @click="startEdit(row)">
                                        Upravit hodnotu
                                    </button>
                                </template>
                            </div>

                            <span v-if="row.isNew" class="badge rounded-pill text-bg-primary flex-shrink-0">nové</span>
                        </div>
                    </div>
                </template>

                <template v-if="found.same.length > 0">
                    <button type="button" class="btn btn-link btn-sm px-0 mt-3" @click="sameShown = !sameShown">
                        Shodné s formulářem ({{ found.same.length }})
                        <span class="fas ms-2" :class="sameShown ? 'fa-chevron-up' : 'fa-chevron-down'"></span>
                    </button>
                    <div v-if="sameShown" class="list-group">
                        <div v-for="row in found.same" :key="row.name" class="list-group-item d-flex gap-3 align-items-start">
                            <div class="found-values-label flex-shrink-0 fw-semibold">{{ parameterName(row.name) }}</div>
                            <div class="flex-grow-1 min-width-0">
                                {{ formatValue(row.name, row.current) }}
                                <FoundValueOrigins :origins="row.origins" :source-names="sourceNames"/>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
        </template>
        <template v-slot:buttons>
            <button v-if="saved !== null" type="button" class="btn btn-primary" data-bs-dismiss="modal">Zavřít</button>
            <template v-else>
                <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" :disabled="saving">Zavřít</button>
                <button type="button" class="btn btn-primary" :disabled="saving || selectedCount === 0" @click="save">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2 align-middle" role="status"></span>
                    Uložit vybrané ({{ selectedCount }})
                </button>
            </template>
        </template>
    </Dialog>
</template>

<script lang="ts">
import Dialog from '@/components/common/Dialog.vue';
import {ParameterList, ParameterValues} from '@/model/api/parameters';
import {computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {plural} from '../common/photos/texts';
import {
    compareStatedArea,
    composeFloorArea,
    FoundPart,
    isPartList,
    PartChoice,
    partArea,
    PartTarget,
    RawPart,
} from './floorAreaParts';
import FoundValueOrigins from './FoundValueOrigins.vue';
import {resolveControl} from '../common/controls/controlComponents';
import {
    buildFoundRows,
    defaultSelection,
    FoundOption,
    FoundParameter,
    FoundRow,
    FoundRows,
    RowSelection,
    selectedValues,
    valuesEqual,
} from './foundValues';

export interface FoundValuesContext {
    found: FoundParameter[];
    definitions: ParameterList;
    /** The values of the form, also those not saved yet */
    current: ParameterValues;
    segments: {label: string, parameterNames: string[]}[];
    /** Names of the sources by their ids, in their order */
    sourceNames: Record<string, string>;
}

/** Required parameters of a segment of the form that are still empty */
export interface MissingParameters {
    label: string;
    path: string;
    names: string[];
}

/**
 * Saves the picked values, gives the message of a failure or the required parameters still empty
 */
export type FoundValuesSaveHandler = (values: ParameterValues, names: string[]) => Promise<{error: string} | {missing: MissingParameters[]}>;

export default defineComponent({
    components: {Dialog, FoundValueOrigins},
    props: {
        saveHandler: {
            type: Function as PropType<FoundValuesSaveHandler>,
            required: true,
        },
    },
    emits: ['closed', 'show-segment'],
    setup(props, {emit, expose}) {
        const {t} = useI18n();

        const dialog = ref<InstanceType<typeof Dialog> | null>(null);
        const context = ref<FoundValuesContext>({found: [], definitions: {}, current: {}, segments: [], sourceNames: {}});
        const sourceId = ref<string | null>(null);
        const onlyParameter = ref<string | null>(null);
        const selections = ref<Record<string, RowSelection>>({});
        const sameShown = ref(false);
        const expandedParts = ref(new Set<string>());
        // values the user edits before taking them over, by the names of the parameters
        const edits = ref<Record<string, {initial: any, value: any, valid: boolean}>>({});
        const saving = ref(false);
        const errorMessage = ref<string | null>(null);
        // after the save: how many values were saved and what is still to fill in
        const saved = ref<{count: number, missing: MissingParameters[]} | null>(null);

        const sourceNames = computed(() => context.value.sourceNames);

        const sourceOptions = computed(() => Object.entries(context.value.sourceNames)
            .filter(([id]) => context.value.found.some(parameter => parameter.values
                .some(value => value.origins.some(origin => origin.sourceId === id))))
            .map(([id, name]) => ({id, name})));

        const found = computed<FoundRows>(() => buildFoundRows(
            onlyParameter.value === null
                ? context.value.found
                : context.value.found.filter(parameter => parameter.name === onlyParameter.value),
            context.value.definitions,
            context.value.current,
            sourceId.value,
        ));

        const groups = computed(() => {
            const rowsByName = new Map(found.value.rows.map(row => [row.name, row]));
            const grouped = context.value.segments
                .map(segment => ({
                    label: segment.label,
                    rows: segment.parameterNames
                        .map(name => rowsByName.get(name))
                        .filter((row): row is FoundRow => row !== undefined),
                }))
                .filter(group => group.rows.length > 0);

            const inSegments = new Set(context.value.segments.flatMap(segment => segment.parameterNames));
            const others = found.value.rows.filter(row => !inSegments.has(row.name));

            return others.length > 0 ? [...grouped, {label: 'Ostatní', rows: others}] : grouped;
        });

        const selectedCount = computed(() => Object.keys(selectedValues(found.value.rows, selections.value)).length);

        // a different filter shows other rows, they start with the safe picks
        watch(found, rows => {
            const next: Record<string, RowSelection> = {};

            for (const row of rows.rows) {
                next[row.name] = selections.value[row.name] ?? defaultSelection(row);
            }

            selections.value = next;
        });

        /**
         * @param onlySourceId shows the values of this source only
         * @param onlyParameterName shows the values of this parameter only
         */
        function show(
            newContext: FoundValuesContext,
            onlySourceId: string | null = null,
            onlyParameterName: string | null = null,
        ): void {
            context.value = newContext;
            sourceId.value = onlySourceId;
            onlyParameter.value = onlyParameterName;
            sameShown.value = false;
            saved.value = null;
            expandedParts.value = new Set();
            edits.value = {};
            errorMessage.value = null;
            selections.value = Object.fromEntries(found.value.rows.map(row => [row.name, defaultSelection(row)]));

            dialog.value!.show();
        }

        function hide(): void {
            dialog.value!.hide();
        }

        function parameterName(name: string): string {
            return t(`parameters.${name}.name`);
        }

        function inputId(row: FoundRow, index: number): string {
            return `found-value-${row.name}-${index}`;
        }

        function isOptionSelected(row: FoundRow, value: any): boolean {
            const selection = selections.value[row.name];

            if (selection?.kind !== 'single') {
                return false;
            }

            return value === undefined ? selection.value === undefined : selection.value !== undefined
                && valuesEqual(selection.value, value);
        }

        // a value picked of the sources replaces the edited one
        function toggleOption(row: FoundRow, value: any): void {
            dropEdit(row);
            selections.value = {
                ...selections.value,
                [row.name]: {kind: 'single', value: !row.conflict && isOptionSelected(row, value) ? undefined : value},
            };
        }

        function clearSelection(row: FoundRow): void {
            dropEdit(row);
            selections.value = {...selections.value, [row.name]: {kind: 'single', value: undefined}};
        }

        function isItemSelected(row: FoundRow, item: any): boolean {
            const selection = selections.value[row.name];

            return selection?.kind === 'multi' && selection.items.some(selected => valuesEqual(selected, item));
        }

        function toggleItem(row: FoundRow, item: any): void {
            const selection = selections.value[row.name];
            const items = selection?.kind === 'multi' ? selection.items : [];

            selections.value = {
                ...selections.value,
                [row.name]: {
                    kind: 'multi',
                    items: isItemSelected(row, item) ? items.filter(selected => !valuesEqual(selected, item)) : [...items, item],
                },
            };
        }

        function definitionOf(name: string): Record<string, any> {
            return context.value.definitions[name] ?? {};
        }

        function startEdit(row: FoundRow): void {
            const selection = selections.value[row.name];
            const initial = (selection?.kind === 'single' ? selection.value : undefined)
                ?? row.options.find(option => option.valid)?.value
                ?? row.current;

            edits.value = {...edits.value, [row.name]: {initial, value: initial, valid: true}};
            selections.value = {...selections.value, [row.name]: {kind: 'single', value: initial}};
        }

        function applyEdit(row: FoundRow, edit: {initial: any, value: any, valid: boolean}): void {
            edits.value = {...edits.value, [row.name]: edit};
            selections.value = {
                ...selections.value,
                // an invalid or empty value is not taken over
                [row.name]: {kind: 'single', value: edit.valid && edit.value !== null ? edit.value : undefined},
            };
        }

        function editInput(row: FoundRow, value: any): void {
            applyEdit(row, {...edits.value[row.name], value});
        }

        function editValidated(row: FoundRow, valid: boolean): void {
            applyEdit(row, {...edits.value[row.name], valid});
        }

        function dropEdit(row: FoundRow): void {
            const others = {...edits.value};
            delete others[row.name];

            edits.value = others;
        }

        function cancelEdit(row: FoundRow): void {
            dropEdit(row);
            selections.value = {...selections.value, [row.name]: defaultSelection(row)};
        }

        function partChoice(row: FoundRow, part: FoundPart): PartChoice {
            const selection = selections.value[row.name];

            return selection?.kind === 'parts' && selection.parts[part.key] !== undefined
                ? selection.parts[part.key]
                : {target: 'skip', name: part.name, rooms: part.rooms.map(() => false)};
        }

        function updatePartChoice(row: FoundRow, part: FoundPart, change: Partial<PartChoice>): void {
            const selection = selections.value[row.name];
            const parts = selection?.kind === 'parts' ? selection.parts : {};

            selections.value = {
                ...selections.value,
                [row.name]: {kind: 'parts', parts: {...parts, [part.key]: {...partChoice(row, part), ...change}}},
            };
        }

        function setPartTarget(row: FoundRow, part: FoundPart, event: Event): void {
            const target = (event.target as HTMLSelectElement).value as PartTarget;
            const choice = partChoice(row, part);

            // a part taken over with no room chosen takes its rooms but the probable duplicates
            updatePartChoice(row, part, {
                target,
                rooms: target !== 'skip' && !choice.rooms.some(Boolean)
                    ? part.rooms.map(room => room.duplicate === null)
                    : choice.rooms,
            });
        }

        function setPartName(row: FoundRow, part: FoundPart, event: Event): void {
            updatePartChoice(row, part, {name: (event.target as HTMLInputElement).value});
        }

        function togglePartRoom(row: FoundRow, part: FoundPart, index: number): void {
            const rooms = [...partChoice(row, part).rooms];
            rooms[index] = !rooms[index];

            updatePartChoice(row, part, {rooms});
        }

        function isPartExpanded(key: string): boolean {
            return expandedParts.value.has(key);
        }

        function togglePartExpanded(key: string): void {
            const next = new Set(expandedParts.value);

            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }

            expandedParts.value = next;
        }

        function currentPartNames(row: FoundRow): string[] {
            return isPartList(row.current) ? row.current.map(part => part.name) : [];
        }

        function hasSumOnly(row: FoundRow): boolean {
            return row.current !== null && row.current !== undefined && !isPartList(row.current);
        }

        function composedParts(row: FoundRow): RawPart[] | null {
            const selection = selections.value[row.name];

            return selection?.kind === 'parts' ? composeFloorArea(row.current, row.parts, selection.parts) : null;
        }

        function composedSummary(row: FoundRow): string {
            const parts = composedParts(row) ?? [];
            const total = parts.reduce((sum, part) => sum + partArea(part), 0);

            return parts.map(part => `${part.name} ${formatNumber(partArea(part))} m²`).join(', ')
                + `, celkem ${formatNumber(total)} m²`;
        }

        function statedAreaCheck(part: FoundPart): {matches: boolean, difference: number} | null {
            return compareStatedArea(part);
        }

        function formatSignedNumber(value: number): string {
            return (value > 0 ? '+' : '') + formatNumber(value);
        }

        function duplicateCount(part: FoundPart): number {
            return part.rooms.filter(room => room.duplicate !== null).length;
        }

        function roomLabel(type: string): string {
            return t(`floorArea.roomTypes.${type}`);
        }

        function roomInputId(key: string, index: number): string {
            return `found-room-${key}-${index}`;
        }

        function optionNote(row: FoundRow, option: FoundOption): string | null {
            if (option.equalsCurrent) {
                return 'shodné s formulářem';
            }

            if (!option.valid) {
                return 'nelze převzít, hodnota neodpovídá formuláři';
            }

            return row.current !== null && row.current !== undefined ? 'přepíše vyplněnou hodnotu' : null;
        }

        function formatItem(name: string, item: any): string {
            const type: string | undefined = context.value.definitions[name]?.type;

            if (item !== null && typeof item === 'object') {
                switch (type) {
                    case 'parcel-numbers':
                        return [`p. č. ${item.number}`, item.note].filter(Boolean).join(', ');
                    case 'reconstruction-list':
                        return [
                            t(`reconstructions.types.${item.type}`),
                            item.year,
                            t(`reconstructions.extents.${item.extent}`),
                            item.description,
                        ].filter(Boolean).join(', ');
                    case 'access-land-list':
                        return [
                            `p. č. ${item.prefixNumber}${item.suffixNumber !== null && item.suffixNumber !== undefined ? '/' + item.suffixNumber : ''}`,
                            t(`accessLands.landTypes.${item.landType}`),
                            item.description,
                        ].filter(Boolean).join(', ');
                }
            }

            if (item !== null && typeof item === 'object' && 'option' in item) {
                return `${t(`parameters.${name}.options.${item.option}`)} (${item.customValue})`;
            }

            if (type === 'multi-text') {
                return String(item);
            }

            return typeof item === 'string' ? t(`parameters.${name}.options.${item}`) : String(item);
        }

        function formatNumber(value: number): string {
            return value.toLocaleString('cs-CZ', {maximumFractionDigits: 2});
        }

        // a number without a unit is a year or a floor, "2021" not "2 021"
        function formatPlainNumber(value: number): string {
            return value.toLocaleString('cs-CZ', {maximumFractionDigits: 2, useGrouping: false});
        }

        function roomArea(dimensions: Array<number | [number, number]>): number {
            return dimensions.reduce<number>(
                (sum, dimension) => sum + (Array.isArray(dimension) ? dimension[0] * dimension[1] : dimension),
                0,
            );
        }

        function formatValue(name: string, value: any): string {
            // the definitions name more types than they declare, e.g. parcel numbers and dates
            const type: string | undefined = context.value.definitions[name]?.type;

            if (value === null || value === undefined) {
                return 'nevyplněno';
            }

            switch (type) {
                case 'enum':
                    return formatItem(name, value);
                case 'multi-enum':
                    return (value as any[]).map(item => formatItem(name, item)).join(', ');
                case 'number': {
                    const unit = (context.value.definitions[name] as {unit?: string | null} | undefined)?.unit;

                    return unit ? `${formatNumber(value)} ${unit}` : formatPlainNumber(value);
                }
                case 'number-or-range':
                    return Array.isArray(value) && value[0] !== value[1]
                        ? `${formatPlainNumber(value[0])} až ${formatPlainNumber(value[1])}`
                        : formatPlainNumber(Array.isArray(value) ? value[0] : value);
                case 'floor-area':
                    if (Array.isArray(value)) {
                        const area = value.reduce(
                            (sum: number, part: any) => sum + (part.rooms ?? []).reduce(
                                (partSum: number, room: any) => partSum + roomArea(room.dimensions ?? []), 0,
                            ),
                            0,
                        );

                        return `${value.map((part: any) => part.name).join(', ')}, celkem ${formatNumber(area)} m²`;
                    }

                    return `${formatNumber(value.gross)} m², bez příslušenství ${formatNumber(value.net)} m²`;
                case 'parcel-numbers':
                    return (value as {number: string}[]).map(parcel => parcel.number).join(', ');
                case 'reconstruction-list':
                case 'access-land-list':
                case 'multi-text':
                    return (value as any[]).map(item => formatItem(name, item)).join('; ');
                case 'date':
                    return new Date(value).toLocaleDateString('cs-CZ');
            }

            if (Array.isArray(value)) {
                return value.every(item => typeof item === 'string') ? value.join(', ') : `${value.length} položek`;
            }

            const text = String(value);

            return text.length > 120 ? text.slice(0, 120) + '…' : text;
        }

        async function save(): Promise<void> {
            const values = selectedValues(found.value.rows, selections.value);

            saving.value = true;
            const result = await props.saveHandler(values, Object.keys(values));
            saving.value = false;

            if ('error' in result) {
                errorMessage.value = result.error;
                return;
            }

            errorMessage.value = null;

            if (result.missing.length === 0) {
                hide();
                return;
            }

            saved.value = {count: Object.keys(values).length, missing: result.missing};
        }

        function showSegment(path: string): void {
            emit('show-segment', path);
            hide();
        }

        function onHidden(): void {
            // the values shown are seen, they are no longer offered as new
            const shown = new Set(found.value.rows.flatMap(row => [
                ...row.options.flatMap(option => option.origins),
                ...row.items.flatMap(item => item.origins),
                ...row.parts.flatMap(part => part.origins),
            ]).map(origin => origin.sourceId));

            emit('closed', [...shown]);
        }

        let modalElement: HTMLElement | null = null;

        onMounted(() => {
            modalElement = (dialog.value?.$el as HTMLElement | undefined) ?? null;
            modalElement?.addEventListener('hidden.bs.modal', onHidden);
        });

        onBeforeUnmount(() => modalElement?.removeEventListener('hidden.bs.modal', onHidden));

        expose({show, hide});

        return {
            dialog,
            sourceId,
            sourceOptions,
            sourceNames,
            found,
            groups,
            sameShown,
            saving,
            errorMessage,
            saved,
            showSegment,
            selectedCount,
            show,
            hide,
            parameterName,
            inputId,
            isOptionSelected,
            toggleOption,
            clearSelection,
            isItemSelected,
            toggleItem,
            optionNote,
            edits,
            resolveControl,
            definitionOf,
            startEdit,
            editInput,
            editValidated,
            cancelEdit,
            partChoice,
            setPartTarget,
            setPartName,
            togglePartRoom,
            isPartExpanded,
            togglePartExpanded,
            currentPartNames,
            hasSumOnly,
            composedParts,
            composedSummary,
            duplicateCount,
            statedAreaCheck,
            formatSignedNumber,
            roomLabel,
            roomInputId,
            plural,
            formatNumber,
            formatItem,
            formatValue,
            save,
        };
    },
});
</script>
